import React from 'react';
import { styled } from '@mui/material';
import { Link } from 'react-router-dom';
import { Colors } from 'library';

type RLinkSize = 'sm' | 'md';
type RLinkWeight = 'medium' | 'semibold';

type RLinkBaseProps = {
    children: React.ReactNode;
    size?: RLinkSize;
    weight?: RLinkWeight;
    underline?: boolean;
    color?: string;
    className?: string;
};

type RLinkInternalProps = RLinkBaseProps & { to: string; href?: never };
type RLinkExternalProps = RLinkBaseProps & { href: string; to?: never };
type RLinkProps = RLinkInternalProps | RLinkExternalProps;

const SIZE_PX: Record<RLinkSize, number> = { sm: 13, md: 14 };
const WEIGHT_VALUE: Record<RLinkWeight, number> = { medium: 500, semibold: 600 };

export function RLink({
    children,
    size = 'md',
    weight = 'semibold',
    underline = true,
    color,
    className,
    ...rest
}: RLinkProps) {
    if ('href' in rest && rest.href) {
        return (
            <ExternalLink
                href={rest.href}
                $size={size}
                $weight={weight}
                $underline={underline}
                $color={color}
                target="_blank"
                rel="noopener noreferrer"
                className={className}
            >
                {children}
            </ExternalLink>
        );
    }
    return (
        <InternalLink
            to={(rest as RLinkInternalProps).to}
            $size={size}
            $weight={weight}
            $underline={underline}
            $color={color}
            className={className}
        >
            {children}
        </InternalLink>
    );
}

type LinkStyleProps = { $size: RLinkSize; $weight: RLinkWeight; $underline: boolean; $color?: string };

const linkStyles = ({ $size, $weight, $underline, $color }: LinkStyleProps) => {
    const resolvedColor = $color ?? Colors.darkNavy;
    return {
        fontSize: SIZE_PX[$size],
        fontWeight: WEIGHT_VALUE[$weight],
        color: resolvedColor,
        textDecoration: $underline ? ('underline' as const) : ('none' as const),
        '&:hover': { color: resolvedColor, opacity: 0.7 },
    };
};

const shouldForwardLinkProp = (prop: string) => !['$size', '$weight', '$underline', '$color'].includes(prop);

const InternalLink = styled(Link, {
    shouldForwardProp: (prop) => shouldForwardLinkProp(prop as string),
})<LinkStyleProps>(linkStyles);

const ExternalLink = styled('a', {
    shouldForwardProp: (prop) => shouldForwardLinkProp(prop as string),
})<LinkStyleProps>(linkStyles);
